<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ArrowRight,
  LoaderCircle,
  MonitorCheck,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-vue-next'
import { useLiveStore } from '@/stores/live'

const MINIMUM_VISIBLE_MS = 650
const emit = defineEmits<{ 'visibility-change': [occupied: boolean] }>()

const { t } = useI18n()
const live = useLiveStore()
const gateComplete = (status: typeof live.ocrStatus | undefined) => status === 'idle' || status === 'ready'
const captureIsConnecting = computed(() => live.captureStatus === 'selected' || live.captureStatus === 'connecting')
const waitingForOcrStart = computed(() => live.captureStatus === 'active' && live.ocrStatus === 'idle')
const capturePreparationActive = computed(() => captureIsConnecting.value || waitingForOcrStart.value)
const visible = ref(false)
const dismissed = ref(false)
let openedAt: number | null = null
let hideRequested = false
let hideTimer: number | undefined
let focusBeforeDialog: HTMLElement | null = null

const shouldShow = computed(() => visible.value && !dismissed.value)
watch(shouldShow, (showing) => {
  if (showing) emit('visibility-change', true)
}, { flush: 'sync' })
const setupIsBusy = computed(() => live.ocrStatus !== 'error'
  && (capturePreparationActive.value || !gateComplete(live.ocrStatus)))
const stageLabel = computed(() => {
  if (live.ocrStatus === 'error') return t('startup.errorStatus')
  if (capturePreparationActive.value) return t('startup.connectingFrame')
  return t(`startup.${live.ocrStage}`)
})
const startupTitle = computed(() => live.ocrStatus === 'initializing'
  ? t('startup.initializingTitle')
  : t('startup.title'))
const startupSubtitle = computed(() => live.ocrStatus === 'initializing'
  ? t('startup.initializingSubtitle')
  : capturePreparationActive.value
    ? t('startup.connectingSubtitle')
    : t('startup.subtitle'))
const localHint = computed(() => live.ocrStatus === 'initializing'
  ? t('startup.initializingLocalHint')
  : t('startup.localHint'))
const errorTitle = computed(() => t(`startup.${live.ocrErrorType ?? 'unknown'}ErrorTitle`))
const errorHint = computed(() => t(`startup.${live.ocrErrorType ?? 'unknown'}ErrorHint`))
const progressValue = computed(() => Math.min(100, Math.max(0, live.ocrProgress)))

function clearHideTimer(): void {
  if (hideTimer === undefined) return
  window.clearTimeout(hideTimer)
  hideTimer = undefined
}

function show(): void {
  clearHideTimer()
  hideRequested = false
  if (visible.value) return
  const activeElement = document.activeElement
  focusBeforeDialog = activeElement instanceof HTMLElement && activeElement !== document.body
    ? activeElement
    : null
  openedAt = null
  visible.value = true
}

function hideWhenStable(): void {
  clearHideTimer()
  hideRequested = true
  if (openedAt === null) return
  const delay = Math.max(0, MINIMUM_VISIBLE_MS - (Date.now() - openedAt))
  hideTimer = window.setTimeout(() => {
    hideTimer = undefined
    visible.value = false
  }, delay)
}

function markDialogOpened(): void {
  openedAt = Date.now()
  if (hideRequested) hideWhenStable()
}

function retry(): void {
  dismissed.value = false
  hideRequested = false
  openedAt = Date.now()
  visible.value = true
  void live.initializeOcr()
}

function dismiss(): void {
  clearHideTimer()
  dismissed.value = true
  visible.value = false
}

function restoreDialogFocus(): void {
  const previous = focusBeforeDialog
  focusBeforeDialog = null
  openedAt = null
  hideRequested = false
  queueMicrotask(() => {
    const target = previous?.isConnected
      ? previous
      : document.querySelector<HTMLElement>('[data-capture-window-action]:not([disabled])')
        ?? document.querySelector<HTMLElement>('#main-content')
    target?.focus({ preventScroll: true })
    if (!shouldShow.value) emit('visibility-change', false)
  })
}

function requiresStartupDialog(
  captureStatus: typeof live.captureStatus | undefined,
  status: typeof live.ocrStatus | undefined,
): boolean {
  if (captureStatus === undefined && status === undefined) return false
  return captureStatus === 'connecting'
    || captureStatus === 'selected'
    || (captureStatus === 'active' && status === 'idle')
    || !gateComplete(status)
}

watch([() => live.captureStatus, () => live.ocrStatus], ([captureStatus, status], previous = [undefined, undefined]) => {
  const [previousCaptureStatus, previousOcrStatus] = previous
  const required = requiresStartupDialog(captureStatus, status)
  const wasRequired = requiresStartupDialog(previousCaptureStatus, previousOcrStatus)
  if (!required) {
    if (visible.value) hideWhenStable()
    return
  }
  if (!wasRequired) dismissed.value = false
  if (!dismissed.value) show()
}, { immediate: true })

onBeforeUnmount(() => {
  clearHideTimer()
  emit('visibility-change', false)
})
</script>

<template>
  <el-dialog
    :model-value="shouldShow"
    class="ocr-startup-dialog"
    :class="{ 'is-error': live.ocrStatus === 'error' }"
    modal-class="ocr-startup-overlay"
    width="28rem"
    align-center
    append-to-body
    destroy-on-close
    lock-scroll
    trap-focus
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    transition="ocr-dialog-fade"
    :aria-busy="setupIsBusy"
    @opened="markDialogOpened"
    @closed="restoreDialogFocus"
  >
    <template #header="{ titleId, titleClass }">
      <div class="startup-heading">
        <span
          class="startup-status-icon"
          :class="live.ocrStatus === 'error' ? 'error' : 'loading'"
          aria-hidden="true"
        >
          <TriangleAlert v-if="live.ocrStatus === 'error'" />
          <LoaderCircle v-else />
        </span>
        <div class="startup-heading-copy">
          <span v-if="live.captureStatus === 'selected' || live.captureStatus === 'connecting' || live.captureStatus === 'active'" class="connection-state">
            <MonitorCheck aria-hidden="true" />
            {{ t(live.captureStatus === 'active' ? 'startup.windowConnected' : 'startup.windowSelected') }}
          </span>
          <h2 :id="titleId" :class="[titleClass, 'startup-title']">
            {{ live.ocrStatus === 'error' ? errorTitle : startupTitle }}
          </h2>
        </div>
      </div>
    </template>

    <p class="startup-description">
      {{ live.ocrStatus === 'error' ? errorHint : startupSubtitle }}
    </p>
    <p
      v-if="live.ocrStatus === 'error' && live.ocrErrorType === 'initialization'"
      class="startup-description startup-browser-version"
    >
      {{ t('startup.browserVersionHint') }}
    </p>

    <div class="startup-progress">
      <div class="progress-status" aria-live="polite" aria-atomic="true">
        <span class="stage-label">
          <TriangleAlert v-if="live.ocrStatus === 'error'" aria-hidden="true" />
          <span v-else class="status-dot" aria-hidden="true" />
          <Transition name="stage-swap" mode="out-in">
            <strong :key="`${live.ocrStatus}:${live.ocrStage}`">{{ stageLabel }}</strong>
          </Transition>
        </span>
        <output>{{ progressValue }}%</output>
      </div>
      <div
        class="progress-track"
        role="progressbar"
        :aria-label="t('startup.progress')"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="progressValue"
      >
        <span class="progress-value" :style="{ transform: `scaleX(${progressValue / 100})` }" />
      </div>
    </div>

    <div v-if="live.ocrStatus === 'error'" class="browser-recommendation" role="note">
      <span class="supporting-icon" aria-hidden="true"><MonitorCheck /></span>
      <span>
        <strong>{{ t('startup.browserRecommendationTitle') }}</strong>
        <small>{{ t('startup.browserRecommendationHint') }}</small>
      </span>
    </div>
    <div v-else class="local-processing-note" role="note">
      <span class="supporting-icon" aria-hidden="true"><ShieldCheck /></span>
      <span>
        <strong>{{ t('startup.localProcessing') }}</strong>
        <small>{{ localHint }}</small>
      </span>
    </div>

    <div v-if="live.ocrStatus === 'error'" class="startup-actions">
      <el-button type="primary" @click="retry">
        <RefreshCw aria-hidden="true" />{{ t('startup.retry') }}
      </el-button>
      <el-button @click="dismiss">
        {{ t('startup.continue') }}<ArrowRight aria-hidden="true" />
      </el-button>
    </div>
  </el-dialog>
</template>

<style scoped>
:global(.ocr-startup-overlay.el-overlay) {
  --ocr-startup-scrim: rgb(8 12 11 / 46%);
  background: var(--ocr-startup-scrim);
}
:global(:root[data-theme='dark'] .ocr-startup-overlay.el-overlay) {
  --ocr-startup-scrim: rgb(0 0 0 / 58%);
}
:global(.ocr-startup-overlay .el-overlay-dialog) {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}
:global(.ocr-startup-dialog.el-dialog) {
  width: min(28rem, calc(100vw - 1.5rem)) !important;
  max-height: calc(100dvh - 1.5rem);
  flex: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  contain: layout paint;
  border: 0.0625rem solid var(--border-strong);
  border-radius: var(--radius-card);
  background: var(--surface-raised);
  box-shadow: var(--shadow-raised);
}
:global(.ocr-startup-dialog .el-dialog__header) {
  margin: 0;
  padding: 1.25rem 1.25rem 0;
}
:global(.ocr-startup-dialog .el-dialog__body) {
  min-height: 0;
  padding: 0.875rem 1.25rem 1.25rem;
  overflow-y: auto;
  color: var(--text);
}
.startup-heading {
  min-width: 0;
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr);
  align-items: start;
  gap: 0.875rem;
}
.startup-status-icon {
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  border-radius: var(--radius-card);
  color: var(--primary);
  background: var(--primary-soft);
}
.startup-status-icon > svg { width: 1.25rem; height: 1.25rem; }
.startup-status-icon.loading > svg { animation: loader-spin 1s linear infinite; }
.startup-status-icon.error {
  color: var(--danger);
  background: color-mix(in srgb, var(--danger) 9%, var(--surface));
}
.startup-heading-copy { min-width: 0; }
.connection-state {
  min-height: 1.125rem;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--green);
  font-size: var(--font-caption);
  line-height: 1.35;
  font-weight: 650;
}
.connection-state > svg { width: 0.875rem; height: 0.875rem; flex: none; }
.startup-title {
  margin: 0.25rem 0 0;
  color: var(--text-strong);
  font-size: 1.125rem;
  line-height: 1.3;
  font-weight: 720;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.startup-description {
  margin: 0;
  color: var(--muted-strong);
  font-size: var(--font-body);
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.startup-browser-version { margin-top: 0.5rem; }
.startup-progress {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 0.0625rem solid var(--border);
}
.progress-status {
  min-height: 1.375rem;
  margin-bottom: 0.5625rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.stage-label {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text);
  font-size: var(--font-label);
  line-height: 1.35;
}
.stage-label > svg { width: 1rem; height: 1rem; flex: none; color: var(--danger); }
.status-dot {
  width: 0.5rem;
  height: 0.5rem;
  margin-top: 0.1875rem;
  flex: none;
  border-radius: 50%;
  background: var(--green);
  animation: status-breathe 2.2s ease-in-out infinite;
}
.stage-label strong { font-weight: 650; overflow-wrap: anywhere; }
.progress-status output {
  min-width: 2.75rem;
  flex: none;
  color: var(--text-strong);
  font-size: var(--font-label);
  line-height: 1.35;
  font-weight: 720;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.progress-track {
  height: 0.375rem;
  overflow: hidden;
  border-radius: 62.4375rem;
  background: var(--surface-subtle);
}
.progress-value {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  border-radius: inherit;
  background: var(--green);
  transition: transform 220ms var(--ease-out);
}
:global(.ocr-startup-dialog.is-error .progress-value) { background: var(--danger); }
.browser-recommendation,
.local-processing-note {
  margin-top: 1rem;
  padding: 0.75rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.6875rem;
  border-radius: var(--radius-card);
  background: var(--surface-subtle);
  text-align: left;
}
.supporting-icon {
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--green);
  background: var(--green-soft);
}
.browser-recommendation .supporting-icon { color: var(--blue); background: var(--blue-soft); }
.supporting-icon > svg { width: 1rem; height: 1rem; }
.browser-recommendation strong,
.browser-recommendation small,
.local-processing-note strong,
.local-processing-note small { display: block; }
.browser-recommendation strong,
.local-processing-note strong {
  color: var(--text-strong);
  font-size: var(--font-label);
  line-height: 1.4;
  font-weight: 680;
}
.browser-recommendation small,
.local-processing-note small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: var(--font-caption);
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.startup-actions {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.startup-actions :deep(.el-button svg) { width: 1rem; height: 1rem; }

:global(.ocr-dialog-fade-enter-active) { transition: opacity 180ms ease; }
:global(.ocr-dialog-fade-leave-active) { transition: opacity 130ms ease; }
:global(.ocr-dialog-fade-enter-active .ocr-startup-dialog) {
  transition: opacity 180ms ease, transform 180ms var(--ease-out);
}
:global(.ocr-dialog-fade-leave-active .ocr-startup-dialog) {
  transition: opacity 100ms ease, transform 130ms ease;
}
:global(.ocr-dialog-fade-enter-from),
:global(.ocr-dialog-fade-leave-to) { opacity: 0; }
:global(.ocr-dialog-fade-enter-from .ocr-startup-dialog) {
  opacity: 0;
  transform: translateY(0.375rem) scale(0.985);
}
:global(.ocr-dialog-fade-leave-to .ocr-startup-dialog) {
  opacity: 0;
  transform: translateY(-0.1875rem) scale(0.99);
}
.stage-swap-enter-active { transition: opacity 150ms ease, transform 170ms var(--ease-out); }
.stage-swap-leave-active { transition: opacity 90ms ease, transform 100ms ease; }
.stage-swap-enter-from { opacity: 0; transform: translateY(0.1875rem); }
.stage-swap-leave-to { opacity: 0; transform: translateY(-0.125rem); }

@keyframes loader-spin { to { transform: rotate(360deg); } }
@keyframes status-breathe {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}

@media (max-width: 32rem) {
  :global(.ocr-startup-overlay .el-overlay-dialog) { padding: 0.75rem; }
  :global(.ocr-startup-dialog .el-dialog__header) { padding: 1.125rem 1rem 0; }
  :global(.ocr-startup-dialog .el-dialog__body) { padding: 0.8125rem 1rem 1rem; }
  .startup-heading { grid-template-columns: 2.5rem minmax(0, 1fr); gap: 0.75rem; }
  .startup-status-icon { width: 2.5rem; height: 2.5rem; }
  .startup-actions { grid-template-columns: 1fr; display: grid; }
  .startup-actions :deep(.el-button) { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .startup-status-icon.loading > svg,
  .status-dot { animation: none; }
  :global(.ocr-dialog-fade-enter-active),
  :global(.ocr-dialog-fade-leave-active),
  :global(.ocr-dialog-fade-enter-active .ocr-startup-dialog),
  :global(.ocr-dialog-fade-leave-active .ocr-startup-dialog),
  .stage-swap-enter-active,
  .stage-swap-leave-active,
  .progress-value { transition: none; }
}
</style>
